import React from "react";
import { useContent } from "../context/ContentContext";
import BrandLogo from "./BrandLogo";
import SEO from "./SEO";

const COLLECTIVE_IMG = "/team/collective.webp";

/**
 * The Collective — group portrait figure, centered and untinted.
 */
const CollectiveSection = () => (
  <section
    className="py-16 md:py-24 border-t border-ink/10 bg-canvas"
    data-testid="collective-section"
  >
    <div className="w-full flex justify-center">
      <figure
        className="relative w-full max-w-[1400px] mx-auto"
        data-testid="collective-figure"
      >
        <img
          src={COLLECTIVE_IMG}
          alt="The SAMURAI collective — portrait grid"
          loading="lazy"
          className="w-full h-auto block"
        />
      </figure>
    </div>
  </section>
);

const About = () => {
  const { TEAM, BRAND_LOGOS, ABOUT_COPY } = useContent();

  return (
    <div className="bg-canvas text-ink min-h-screen">
      <SEO
        title="About SAMURAI · The Collective Behind the Films"
        description="SAMURAI is a collective of filmmakers, artists, producers and technologists redefining filmmaking with generative AI as a cinematic tool. Meet the team."
        path="/about"
      />
      {/* Centered page header */}
      <section
        className="pt-32 md:pt-40 pb-10 md:pb-14 bg-canvas relative"
        data-testid="about-page-header"
      >
        <div className="mx-auto px-6 md:px-10 text-center">
          <p className="text-[10px] tracking-[0.32em] uppercase text-ink/55 mb-4">
            About
          </p>
          <h1 className="font-heading font-black text-[44px] md:text-[88px] leading-[0.92] tracking-[-0.03em] uppercase fade-up">
            The{" "}
            <span className="italic font-serif font-normal text-ink/70 normal-case">
              Collective.
            </span>
          </h1>
        </div>
      </section>

      {/* The Collective — group portrait section */}
      <CollectiveSection />

      {/* Body copy — sits below the portrait */}
      <section className="py-16 md:py-24 border-t border-ink/10">
        <div className="mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-8">
          <p className="md:col-span-3 text-[10px] tracking-[0.32em] uppercase text-ink/55">
            About
          </p>
          <div
            className="md:col-span-9 max-w-3xl space-y-5 text-sm md:text-base text-ink/85 leading-[1.55] font-light tracking-[-0.005em]"
            style={{ fontFamily: "'Geist', 'Inter', ui-sans-serif, system-ui, sans-serif" }}
            data-testid="about-copy"
          >
            {ABOUT_COPY.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>
        </div>
      </section>

      {/* Who we are */}
      <section className="py-20 border-t border-ink/10">
        <div className="mx-auto px-6 md:px-10">          <p className="text-[10px] tracking-[0.32em] uppercase text-ink/55 mb-12">
            Who we are
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-8">
            {TEAM.map((t) => (
              <div
                key={t.id}
                className="border-b border-ink/10 pb-6"
              >
                <h3 className="font-heading font-black text-xs md:text-lg tracking-[-0.02em] leading-tight uppercase">
                  {t.name}
                </h3>
                <p className="text-[10px] text-ink/60 mt-1.5 tracking-[0.22em] uppercase">
                  {t.role}
                </p>
                {t.email && (
                  <a
                    href={`mailto:${t.email}`}
                    className="mt-1.5 inline-block text-[11px] text-ink/70 hover:text-ink tracking-[0.02em] transition-colors"
                    style={{ fontFamily: "'Geist', 'Inter', ui-sans-serif, system-ui, sans-serif" }}
                    data-testid={`team-email-${t.id}`}
                  >
                    {t.email}
                  </a>
                )}
              </div>
            ))}
          </div>

          <p className="mt-12 text-sm text-ink/60 max-w-xl">
            SAMURAI is an open collective. The roster grows with every project.
            If you’re a director, DoP, editor or AI artist,{" "}
            <a
              href="/contact"
              className="underline underline-offset-4 hover:text-ink"
            >
              apply as an artist
            </a>
            .
          </p>
        </div>
      </section>

      {/* Partners & Collaborations */}
      <section className="py-20 border-t border-ink/10">
        <div className="mx-auto px-6 md:px-10">
          <p className="text-[10px] tracking-[0.32em] uppercase text-ink/55 mb-10">
            Partners &amp; Collaborations
          </p>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 lg:grid-cols-6 gap-x-6 gap-y-12 items-center">
            {BRAND_LOGOS.map((b) => (
              <BrandLogo
                key={b.name}
                name={b.name}
                logo={b.logo}
                domain={b.domain}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Partners section is now the last section; contact tail moved to global footer */}
    </div>
  );
};

export default About;
